import React, { useMemo, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ProfileSectionTitle } from "../profile/ProfileSectionTitle";
import { MI_CONTENIDO_DAILY_TRENDS_CHART_MIN_DATE } from "../../miContenido/contentTrends/dailyTrendsConfig";
import { DailyMetricsGrowthAbsoluteChart } from "../../pages/ProgramAnalysis/DailyMetricsGrowthAbsoluteChart";
import {
  buildSingleContentInteractionAbsoluteChartPoints,
  buildSingleContentViewsAbsoluteChartPoints,
  type DailyMetricPoint,
  type DailyMetricsXAxisMode,
} from "../../pages/ProgramAnalysis/dailyMetricsChart";
import { toChileanDateString } from "../../utils/dateUtils";

const CUMULATIVE_LINE_COLOR = "#9b8ae8";

function formatChartCaption(applyMinDateFilter: boolean): string {
  const base =
    "Barras: ganancia diaria · Línea: total acumulado (snapshot collectedAt)";
  if (!applyMinDateFilter) return base;
  const [y, m, d] = MI_CONTENIDO_DAILY_TRENDS_CHART_MIN_DATE.split("-").map(
    Number,
  );
  const date = new Date(y!, m! - 1, d!);
  return `${base}, desde ${date.toLocaleDateString("es-CL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })}`;
}

function filterDailyMetricsByMinDate(
  dailyMetrics: DailyMetricPoint[],
  minDate: string,
): DailyMetricPoint[] {
  return dailyMetrics.filter((m) => {
    const day = toChileanDateString(m.collectedAt);
    return day && day >= minDate;
  });
}

export type ContentDetailDailyMetricsSectionProps = {
  dailyMetrics: DailyMetricPoint[];
  isLoading?: boolean;
  /** When true, hide snapshots before {@link MI_CONTENIDO_DAILY_TRENDS_CHART_MIN_DATE} (short-form only). */
  applyMinDateFilter?: boolean;
  barColor?: string;
  lineColor?: string;
  showCaption?: boolean;
  showXAxisSelect?: boolean;
};

export const ContentDetailDailyMetricsSection: React.FC<
  ContentDetailDailyMetricsSectionProps
> = ({
  dailyMetrics,
  isLoading = false,
  applyMinDateFilter = false,
  barColor: barColorProp,
  lineColor: lineColorProp,
  showCaption = true,
  showXAxisSelect = true,
}) => {
  const theme = useTheme();
  const [xAxisMode, setXAxisMode] = useState<DailyMetricsXAxisMode>("calendar");

  const filteredMetrics = useMemo(() => {
    if (!applyMinDateFilter) return dailyMetrics;
    return filterDailyMetricsByMinDate(
      dailyMetrics,
      MI_CONTENIDO_DAILY_TRENDS_CHART_MIN_DATE,
    );
  }, [dailyMetrics, applyMinDateFilter]);

  const viewsChartData = useMemo(
    () =>
      buildSingleContentViewsAbsoluteChartPoints(filteredMetrics, xAxisMode),
    [filteredMetrics, xAxisMode],
  );

  const interactionsChartData = useMemo(
    () =>
      buildSingleContentInteractionAbsoluteChartPoints(
        filteredMetrics,
        xAxisMode,
      ),
    [filteredMetrics, xAxisMode],
  );

  const barColor = barColorProp ?? theme.palette.podscope.main;
  const lineColor = lineColorProp ?? CUMULATIVE_LINE_COLOR;

  if (!isLoading && dailyMetrics.length === 0) {
    return null;
  }

  const xAxisControl = (
    <FormControl
      size="small"
      sx={{
        minWidth: { xs: 0, sm: 200 },
        width: { xs: "100%", sm: "auto" },
        flexShrink: 0,
      }}
    >
      <InputLabel id="content-detail-daily-xaxis-label">Eje X</InputLabel>
      <Select
        labelId="content-detail-daily-xaxis-label"
        label="Eje X"
        value={xAxisMode}
        onChange={(e) => setXAxisMode(e.target.value as DailyMetricsXAxisMode)}
        MenuProps={{
          disableScrollLock: true,
          slotProps: {
            paper: {
              sx: {
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
              },
            },
          },
        }}
      >
        <MenuItem value="days">Días desde publicación</MenuItem>
        <MenuItem value="calendar">Fecha</MenuItem>
      </Select>
    </FormControl>
  );

  const chartPaperSx = {
    p: 2,
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
  };

  return (
    <Stack spacing={2}>
      <ProfileSectionTitle
        title="Métricas diarias"
        action={showXAxisSelect ? xAxisControl : undefined}
      />
      {showCaption ? (
        <Typography variant="caption" color="text.secondary">
          {formatChartCaption(applyMinDateFilter)}
        </Typography>
      ) : null}

      {isLoading ? (
        <>
          <Paper elevation={0} sx={chartPaperSx}>
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.primary"
              sx={{ mb: 1.5 }}
            >
              Crecimiento y total de views
            </Typography>
            <Skeleton variant="rounded" height={160} />
          </Paper>
          <Paper elevation={0} sx={chartPaperSx}>
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.primary"
              sx={{ mb: 1.5 }}
            >
              Crecimiento y total de interacciones
            </Typography>
            <Skeleton variant="rounded" height={160} />
          </Paper>
        </>
      ) : filteredMetrics.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No hay snapshots de dailyMetrics en el rango mostrado.
        </Typography>
      ) : (
        <>
          {viewsChartData.some(
            (p) => p.incremental > 0 || p.cumulative > 0,
          ) ? (
            <Paper elevation={0} sx={chartPaperSx}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.primary"
                sx={{ mb: 1.5 }}
              >
                Crecimiento y total de views
              </Typography>
              <DailyMetricsGrowthAbsoluteChart
                data={viewsChartData}
                height={160}
                barColor={barColor}
                lineColor={lineColor}
                incrementalLegend="Views ganadas"
                cumulativeLegend="Views totales"
                incrementalTooltipLabel="Views ganadas"
                cumulativeTooltipLabel="Views totales"
              />
            </Paper>
          ) : null}

          {interactionsChartData.some(
            (p) => p.incremental > 0 || p.cumulative > 0,
          ) ? (
            <Paper elevation={0} sx={chartPaperSx}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.primary"
                sx={{ mb: 1.5 }}
              >
                Crecimiento y total de interacciones
              </Typography>
              <DailyMetricsGrowthAbsoluteChart
                data={interactionsChartData}
                height={160}
                barColor={barColor}
                lineColor={lineColor}
                incrementalLegend="Interacciones ganadas"
                cumulativeLegend="Interacciones totales"
                incrementalTooltipLabel="Interacciones ganadas"
                cumulativeTooltipLabel="Interacciones totales"
              />
            </Paper>
          ) : null}

          {!viewsChartData.some(
            (p) => p.incremental > 0 || p.cumulative > 0,
          ) &&
          !interactionsChartData.some(
            (p) => p.incremental > 0 || p.cumulative > 0,
          ) ? (
            <Typography variant="body2" color="text.secondary">
              No hay crecimiento de views ni interacciones en los snapshots
              disponibles.
            </Typography>
          ) : null}
        </>
      )}
    </Stack>
  );
};
